import { useTranslation } from 'react-i18next'

import { EMPTY_STRING } from '../../../../constants/common'
import { HTML_LINK_TARGET } from '../../../../constants/html'
import {
  PROFILE_ROUTE_NAMES,
  SUBMENU_PARAM_KEY,
} from '../../../../constants/route'
import {
  useInitData,
  useMassActions,
  useNotification,
  usePerformMassAction,
  useSubMenuActions,
} from '../../../../hooks'
import { stopPropagation } from '../../../../utils/events'
import {
  getEmailProfilePathname,
  getRequestProfilePathname,
} from '../../../../utils/link'
import { ConfirmationModal } from '../../../Confirmation'
import { AttachServiceRequestsModal } from '../../../Request'
import {
  getEmailAccountsMenu,
  getEmailMessages,
  performEmailMassAction,
  updateEmail,
} from '../../Email.api'
import {
  EMAIL_ACTION_ADD_NOTE,
  EMAIL_ACTION_ARCHIVE_ITEM,
  EMAIL_ACTION_ARCHIVE_MASS,
  EMAIL_ACTION_ATTACH_REQUEST,
  EMAIL_ACTION_ATTACH_REQUEST_MASS,
  EMAIL_ACTION_COPY_TO_ARCHIVE_ITEM,
  EMAIL_ACTION_COPY_TO_ARCHIVE_MASS,
  EMAIL_ACTION_CREATE_SERVICE_REQUEST,
  EMAIL_ACTION_MARK_AS_READ_ITEM,
  EMAIL_ACTION_MARK_AS_READ_MASS,
  EMAIL_ACTION_MARK_AS_STARRED_ITEM,
  EMAIL_ACTION_MARK_AS_STARRED_MASS,
  EMAIL_ACTION_MARK_AS_UNREAD_ITEM,
  EMAIL_ACTION_MARK_AS_UNREAD_MASS,
  EMAIL_ACTION_MARK_AS_UNSTARRED_ITEM,
  EMAIL_ACTION_MARK_AS_UNSTARRED_MASS,
  EMAIL_ACTION_OPEN_IN_NEW_TAB,
  EMAIL_ACTION_RESTORE_ITEM,
  EMAIL_ACTION_RESTORE_MASS,
  EMAIL_PROFILE_ACTION_FORWARD,
  EMAIL_PROFILE_ACTION_PRINT,
  EMAIL_PROFILE_ACTION_REPLY,
  EMAIL_PROFILE_ACTION_REPLY_ALL,
  EMAIL_TABLE_MASS_ACTION_NAMES,
  EMAIL_TABLE_ROW_ACTIONS_CONFIG,
} from '../../Email.constants'
import {
  getEmailTableAsyncNotificationText,
  getEmailTableNotificationTitle,
} from '../../Email.utils'
import {
  EmailAddNoteActionModal,
  getEmailAddNoteInitialValues,
} from '../../EmailAddNoteActionModal'
import { useEmailAttachRequest } from '../useEmailAttachRequest'
import { useEmailCopyTo } from '../useEmailCopyTo'
import { useEmailCreateRequestAction } from '../useEmailCreateRequestAction'
import { EMAIL_REPLY_FORM_MODE } from '../useEmailReply'
import { useEmailReplyModal } from '../useEmailReplyModal'

export function useEmailTableActions({
  account,
  excludeIds,
  filterValues,
  pagination,
  selectAllPages,
  selectedIds,
  singleEmailAccount,
  sub,
  onCleanSelection,
  onCloseModal,
  onReload,
  onSetModal,
}) {
  const { setSubMenuAccounts } = useSubMenuActions()
  const { t } = useTranslation()

  const { availableSections } = useInitData()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const {
    getAsyncTaskId,
    handleAddAsyncMassActionNotification,
    handleAddFinishMassActionNotification,
  } = useMassActions({
    getAsyncNotificationText: getEmailTableAsyncNotificationText,
    getNotificationTitle: getEmailTableNotificationTitle,
  })

  const { handleOpenRequestCreationModal, requestCreationModal } =
    useEmailCreateRequestAction({
      onSuccess: handleSuccessRequestCreation,
    })

  const { handleOpenAttachServiceRequestModal } = useEmailAttachRequest({
    successTitle: getEmailTableNotificationTitle({
      action: EMAIL_ACTION_ATTACH_REQUEST,
      t,
    }),
    onCloseModal,
    onSetModal,
    onSuccessAction: handleSuccessAction,
  })

  function handleFinishAndRefreshMenu(data, params) {
    handleAddFinishMassActionNotification(data, params)

    getEmailAccountsMenu().then(response => {
      setSubMenuAccounts(response.data)
    })
  }

  const { isPerformingMassAction, massAction, setPerformingMassAction } =
    usePerformMassAction({
      excludeIds,
      filterValues,
      getAsyncTaskId,
      isUuid: true,
      massActionNames: EMAIL_TABLE_MASS_ACTION_NAMES,
      pagination,
      performApi: params => performEmailMassAction(params, singleEmailAccount),
      selectAllPages,
      selectedIds,
      sub,
      onAddAsyncMassActionNotification: handleAddAsyncMassActionNotification,
      onAddFinishMassActionNotification: handleFinishAndRefreshMenu,
      onCleanSelection,
      onCloseModal,
      onErrorAction: handleErrorAction,
      onReload,
    })

  const { handleOpenCopyToModal } = useEmailCopyTo({
    onCloseModal,
    onSetModal,
  })

  async function getListEmailMessage(item) {
    return getEmailMessages({
      params: { uuid: [item[EMAIL_TABLE_ROW_ACTIONS_CONFIG.itemIdKey]] },
    }).then(response => response.data.items?.[0])
  }

  async function handleSuccessReply() {
    return onReload()
  }

  const { handleOpenReplyModal } = useEmailReplyModal({
    getMessage: getListEmailMessage,
    onCloseModal,
    onSetModal,
    onSetPerformingAction: setPerformingMassAction,
    onSuccess: handleSuccessReply,
  })

  function handleSuccessAction(action, notifications) {
    onCloseModal()

    onReload().finally(() => {
      setPerformingMassAction(false)
    })

    if (notifications?.length > 0) {
      notifications.map(handleAddSuccessNotification)
    } else {
      handleAddSuccessNotification({
        title: getEmailTableNotificationTitle({ action, t }),
      })
    }
  }

  function handleErrorAction(title) {
    onCloseModal()
    handleAddErrorNotification(title)
  }

  function showConfirmModal(title, onOk, btnText) {
    onSetModal(
      <ConfirmationModal
        actionButtonsProps={{
          submitText: btnText,
        }}
        description={title}
        qaName='confirmation'
        onCancel={onCloseModal}
        onSubmit={onOk}
      />
    )
  }

  function setImportant(id, important) {
    setPerformingMassAction(true)
    updateEmail(id, { starred: important }, singleEmailAccount)
      .then(() => {
        handleSuccessAction(
          important
            ? EMAIL_ACTION_MARK_AS_STARRED_ITEM
            : EMAIL_ACTION_MARK_AS_UNSTARRED_ITEM
        )
      })
      .catch(() => {
        handleErrorAction(
          important
            ? 'Notification.EmailMarkAsStarredErrorKey'
            : 'Notification.EmailMarkAsUnstarredErrorKey'
        )
      })
  }

  function handleAddNote(id, values) {
    setPerformingMassAction(true)
    updateEmail(id, { notes: values.notes || null }, singleEmailAccount) // api does not accept empty string for this field
      .then(() => {
        handleSuccessAction(EMAIL_ACTION_ADD_NOTE)
      })
      .catch(() => {
        handleErrorAction('Notification.EmailAddNoteErrorKey')
      })
  }

  function handleSuccessRequestCreation(data) {
    onReload()

    if (data?.id) {
      // wizard / workflow-bot returns no request id, so there is nothing to open
      window.open(
        getRequestProfilePathname({ availableSections, id: data.id }),
        HTML_LINK_TARGET.blank
      )
    }
  }

  function handleSubmitCopyEmailToEntity({ action, item }) {
    return data =>
      massAction({
        ...(item ? { ids: [item.uuid] } : null),
        action,
        value: { entities: data },
      })
  }

  async function handleAttachRequests(value) {
    massAction({
      action: EMAIL_ACTION_ATTACH_REQUEST_MASS,
      value: { request_id: value.id },
    })
  }

  function setItemAction(action, item) {
    switch (action) {
      case EMAIL_ACTION_ADD_NOTE:
        onSetModal(
          <EmailAddNoteActionModal
            id={item[EMAIL_TABLE_ROW_ACTIONS_CONFIG.itemIdKey]}
            initialValues={getEmailAddNoteInitialValues(item.notes)}
            onClose={onCloseModal}
            onSubmit={handleAddNote}
          />
        )
        break
      case EMAIL_ACTION_ARCHIVE_ITEM:
        showConfirmModal(
          t('Modal.EmailArchiveConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({
              action,
              ids: item[EMAIL_TABLE_ROW_ACTIONS_CONFIG.itemIdKey],
            })
          },
          t('Modal.EmailArchiveBtnKey')
        )
        break
      case EMAIL_ACTION_RESTORE_ITEM:
        showConfirmModal(
          t('Modal.EmailRestoreConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({
              action,
              ids: item[EMAIL_TABLE_ROW_ACTIONS_CONFIG.itemIdKey],
            })
          },
          t('Modal.EmailRestoreBtnKey')
        )
        break
      case EMAIL_ACTION_ATTACH_REQUEST:
        handleOpenAttachServiceRequestModal({ item })
        break

      case EMAIL_ACTION_CREATE_SERVICE_REQUEST:
        handleOpenRequestCreationModal(item)
        break
      case EMAIL_ACTION_MARK_AS_READ_ITEM:
      case EMAIL_ACTION_MARK_AS_UNREAD_ITEM:
        massAction({
          action,
          ids: item[EMAIL_TABLE_ROW_ACTIONS_CONFIG.itemIdKey],
        })
        break
      case EMAIL_ACTION_MARK_AS_STARRED_ITEM:
        setImportant(item[EMAIL_TABLE_ROW_ACTIONS_CONFIG.itemIdKey], true)
        break
      case EMAIL_ACTION_MARK_AS_UNSTARRED_ITEM:
        setImportant(item[EMAIL_TABLE_ROW_ACTIONS_CONFIG.itemIdKey], false)
        break
      case EMAIL_ACTION_OPEN_IN_NEW_TAB:
        window.open(
          getEmailProfilePathname({
            availableSections,
            id: item.uuid,
            singleEmailAccount,
            sub,
          })
        )

        break
      case EMAIL_PROFILE_ACTION_PRINT:
        window.open(
          PROFILE_ROUTE_NAMES[
            singleEmailAccount ? 'emailMessagePrint' : 'emailThreadPrint'
          ]({
            additionalParams: sub
              ? `?${SUBMENU_PARAM_KEY}=${sub}`
              : EMPTY_STRING,
            id: item.uuid,
          }),
          HTML_LINK_TARGET.blank
        )
        break
      case EMAIL_PROFILE_ACTION_REPLY:
        handleOpenReplyModal({ item, mode: EMAIL_REPLY_FORM_MODE.reply })
        break
      case EMAIL_PROFILE_ACTION_REPLY_ALL:
        handleOpenReplyModal({ item, mode: EMAIL_REPLY_FORM_MODE.replyAll })
        break
      case EMAIL_PROFILE_ACTION_FORWARD:
        handleOpenReplyModal({ item, mode: EMAIL_REPLY_FORM_MODE.forward })
        break
      case EMAIL_ACTION_COPY_TO_ARCHIVE_ITEM:
        handleOpenCopyToModal({
          item: { ...item, is_private: !account.corporate },
          onSubmit: handleSubmitCopyEmailToEntity({
            action,
            item,
          }),
        })
        break
      default:
        return null
    }
  }

  function setMassAction(action) {
    switch (action) {
      case EMAIL_ACTION_MARK_AS_READ_MASS:
      case EMAIL_ACTION_MARK_AS_UNREAD_MASS:
      case EMAIL_ACTION_MARK_AS_STARRED_MASS:
      case EMAIL_ACTION_MARK_AS_UNSTARRED_MASS:
        massAction({ action })
        break
      case EMAIL_ACTION_ARCHIVE_MASS:
        showConfirmModal(
          t('Modal.EmailMassArchiveConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({ action })
          },
          t('Modal.EmailMassArchiveBtnKey')
        )
        break
      case EMAIL_ACTION_ATTACH_REQUEST_MASS:
        onSetModal(
          <AttachServiceRequestsModal
            onAttach={handleAttachRequests}
            onClose={onCloseModal}
          />
        )
        break
      case EMAIL_ACTION_RESTORE_MASS:
        showConfirmModal(
          t('Modal.EmailMassRestoreConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({ action })
          },
          t('Modal.EmailMassRestoreBtnKey')
        )
        break
      case EMAIL_ACTION_COPY_TO_ARCHIVE_MASS:
        handleOpenCopyToModal({
          item: { is_private: !account.corporate },
          onSubmit: handleSubmitCopyEmailToEntity({ action }),
        })
        break
      default:
        return null
    }
  }

  return {
    isPerformingMassAction,
    requestCreationModal,
    setImportant,
    setItemAction,
    setMassAction,
  }
}
